import { Directive, ElementRef, forwardRef, HostListener, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[appNumericFormatter]',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => NumericFormatterDirective),
    multi: true
  }]
})
export class NumericFormatterDirective implements ControlValueAccessor {
  @Input() decimals = 0;            // 0 para volúmenes, 3 o 4 para densidad
  @Input() disabled = false;

  // Usamos es-AR -> miles con punto, decimales con coma.
  private nf = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 20
  });

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private el: ElementRef<HTMLInputElement>) {}

  // Escribe desde el modelo -> vista (formatea)
  writeValue(value: number | string | null): void {
    const n = this.parseToNumber(value);
    this.el.nativeElement.value = (n ?? null) === null
      ? ''
      : this.formatNumber(n, this.decimals);
  }

  registerOnChange(fn: (value: number | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.el.nativeElement.disabled = isDisabled;
  }

  @HostListener('input', ['$event'])
  onInput(e: Event) {
    const raw = (e.target as HTMLInputElement).value;
    const num = this.parseToNumber(raw);
    this.onChange(num);
  }

  @HostListener('blur')
  onBlur() {
    const num = this.parseToNumber(this.el.nativeElement.value);
    this.el.nativeElement.value = (num ?? null) === null ? '' : this.formatNumber(num, this.decimals);
    this.onTouched();
  }

  @HostListener('focus')
  onFocus() {
    const num = this.parseToNumber(this.el.nativeElement.value);
    this.el.nativeElement.value = (num ?? null) === null ? '' : this.normalize(num, this.decimals);
  }

  private parseToNumber(value: number | string | null): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return isFinite(value) ? value : null;
    const cleaned = String(value).replace(/\./g, '').replace(',', '.').trim();
    const n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
  }

  private formatNumber(n: number, decimals: number): string {
    const fixed = n.toFixed(decimals);         
    const asNumber = parseFloat(fixed);  
    const parts = asNumber.toLocaleString('es-AR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    return parts;
  }

  private normalize(n: number, decimals: number): string {
    const fixed = n.toFixed(decimals).replace('.', ',');
    return fixed.replace(/\./g, '');
  }
}